import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Headline from '../../../ui/Headline';
import Button from '../../../ui/Button';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import Input from '../../../ui/Input';
import Loader from '../../../ui/Loader';
import { copyRallyToAccount, transferRally, } from '../../../api/admin/structure';
const ERROR_KEYS = {
    internal_title_exists: 'admin.structure.error_internal_title_exists',
    no_internal_title: 'admin.structure.error_no_internal_title',
    invalid_target_user: 'admin.structure.error_invalid_target_user',
    same_owner: 'admin.structure.error_same_owner',
};
const RallyAccountDialog = ({ action, overview, onClose, onDone }) => {
    const { t } = useTranslation();
    const isCopy = action.mode === 'copy';
    const [targetUserId, setTargetUserId] = useState(null);
    const [accountFilter, setAccountFilter] = useState('');
    const [internalTitle, setInternalTitle] = useState(`${action.rally.internalTitle} (Kopie)`);
    const [isPending, setIsPending] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    // only active editors receive rallies; a transfer needs a different owner
    const accountOptions = useMemo(() => overview.institutions.flatMap((institution) => institution.accounts
        .filter((account) => account.isActive && account.role === 'user')
        .filter((account) => isCopy || account.id !== action.ownerId)
        .map((account) => ({
        id: account.id,
        institution: institution.name,
        name: `${account.firstName} ${account.lastName}`.trim() || account.email,
        email: account.email,
    }))), [overview, isCopy, action.ownerId]);
    const filterQuery = accountFilter.trim().toLowerCase();
    const visibleOptions = accountOptions.filter((option) => filterQuery === '' ||
        [option.institution, option.name, option.email].some((value) => value.toLowerCase().includes(filterQuery)));
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !isPending) {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isPending, onClose]);
    const handleConfirm = async () => {
        if (targetUserId === null) {
            setErrorKey('admin.structure.error_choose_account');
            return;
        }
        setIsPending(true);
        setErrorKey(null);
        try {
            if (isCopy) {
                await copyRallyToAccount(action.rally.id, {
                    targetUserId,
                    internalTitle: internalTitle.trim(),
                });
                onDone('admin.structure.copy_success');
            }
            else {
                await transferRally(action.rally.id, { targetUserId });
                onDone('admin.structure.transfer_success');
            }
        }
        catch (error) {
            const code = error?.response?.data?.error?.code;
            setErrorKey(ERROR_KEYS[code] ?? 'admin.structure.error_action_failed');
            setIsPending(false);
        }
    };
    return (_jsxs("div", { className: "modal", role: "dialog", "aria-modal": "true", children: [_jsx("div", { className: "modal-backdrop", onClick: () => !isPending && onClose() }), _jsx("div", { className: "modal-panel structure-account-dialog-panel", children: _jsxs("div", { className: "modal-content structure-account-dialog", children: [_jsx("div", { className: "modal-content-title", children: _jsx(Headline, { title: t(isCopy ? 'admin.structure.copy_title' : 'admin.structure.transfer_title'), type: 'h1' }) }), _jsx("p", { className: "modal-content-description", children: t(isCopy ? 'admin.structure.copy_description' : 'admin.structure.transfer_description', {
                                rally: action.rally.internalTitle,
                            }) }), _jsxs("div", { className: "structure-account-dialog-fields", children: [_jsx(Input, { id: "structure-account-filter", label: t('admin.structure.target_account'), placeholder: t('admin.structure.filter_accounts'), value: accountFilter, onChange: (event) => setAccountFilter(event.target.value), disabled: isPending }), _jsxs("ul", { className: "structure-account-options", role: "listbox", "aria-label": t('admin.structure.target_account'), children: [visibleOptions.map((option) => (_jsx("li", { role: "option", "aria-selected": option.id === targetUserId, children: _jsxs("button", { type: "button", className: option.id === targetUserId ? 'is-selected' : '', onClick: () => setTargetUserId(option.id), disabled: isPending, children: [_jsx("span", { className: "structure-account-option-name", children: option.name }), _jsxs("span", { className: "structure-account-option-meta", children: [option.institution, " \u00B7 ", option.email] })] }) }, option.id))), visibleOptions.length === 0 && (_jsx("li", { className: "structure-empty", children: t('admin.structure.no_results') }))] }), isCopy && (_jsx(Input, { id: "structure-copy-internal-title", label: t('admin.structure.new_internal_title'), value: internalTitle, onChange: (event) => setInternalTitle(event.target.value), disabled: isPending }))] }), errorKey && _jsx("p", { className: "form-field-error-message", children: t(errorKey) }), _jsx("div", { className: "modal-content-button", children: isPending ? (_jsx(Loader, {})) : (_jsxs(ButtonsGroup, { justify: 'center', children: [_jsx(Button, { label: t('button.cancel'), variant: 'outlined', onClick: onClose }), _jsx(Button, { label: t(isCopy ? 'admin.structure.copy' : 'admin.structure.transfer'), onClick: handleConfirm })] })) })] }) })] }));
};
export default RallyAccountDialog;
